import { captureException, environment } from '@chilipiper/web-data-external'
import type { AnalyticsSettingsEvent, WebDataEvent } from '../../shared/types'
import { CHILIPIPER_DEANON_CLASS, CHILIPIPER_DEANON_EVENT } from '../../shared/constants'
import { getMode, logger } from './logger'

type IframeHandle = {
  sendEvent: (event: WebDataEvent) => void
  initPromise: Promise<string>
  analyticsSettingsPromise: Promise<AnalyticsSettingsEvent['settings']>
  // The iframe's consent decisions, as they arrive: at its init and on every change. Returns the
  // unsubscribe.
  onConsentDecision: (callback: (enforce: boolean) => void) => () => void
}

type BuildIframeOptions = {
  // The consent state known when the iframe is created; later changes arrive as Set_Consent.
  consentDenied: boolean
}

export const buildIframe = (
  tenantId: string,
  { consentDenied }: BuildIframeOptions
): IframeHandle => {
  const body = document.getElementsByTagName('body')[0]

  const iframeElement = document.createElement('iframe')
  iframeElement.className = CHILIPIPER_DEANON_CLASS
  iframeElement.title = 'Chili Web Data Tracking'
  iframeElement.setAttribute('aria-label', 'Chili Web Data Tracking')
  iframeElement.src =
    environment.baseIframeUrl +
    '/web-data/deanonymization/iframe/' +
    '?tenantId=' +
    encodeURIComponent(tenantId) +
    '&parentOrigin=' +
    encodeURIComponent(window.location.origin) +
    '&debug=' +
    encodeURIComponent(getMode()) +
    '&consentDenied=' +
    encodeURIComponent(String(consentDenied))
  iframeElement.style.border = '0'
  iframeElement.style.overflow = 'hidden'
  iframeElement.style.height = '0'
  iframeElement.style.width = '1px'
  iframeElement.style.minWidth = '0'
  iframeElement.style.top = '0'
  iframeElement.style.left = '0'
  iframeElement.style.right = '0'
  iframeElement.style.bottom = '0'
  iframeElement.style.position = 'fixed'
  iframeElement.style.zIndex = '-1'

  body.appendChild(iframeElement)

  const sendEvent = (event: WebDataEvent) => {
    if (!iframeElement.isConnected) {
      throw new Error('Iframe not in DOM')
    }

    if (!iframeElement.contentWindow) {
      throw new Error('Iframe contentWindow not ready')
    }

    iframeElement.contentWindow.postMessage(
      {
        type: CHILIPIPER_DEANON_EVENT,
        data: JSON.stringify(event),
      },
      '*'
    )
  }

  // Timeout is inlined so the listener is always removed — whether the predicate
  // resolves, throws, or the timeout fires.
  // Signaled failures are reported via the `fail` callback — logs and rejects without going through
  // captureException, since the inner iframe already reports the underlying cause to Sentry.
  const waitForMessage = <T>(
    predicate: (data: WebDataEvent, fail: (err: Error) => void) => T | undefined,
    timeoutMs: number,
    timeoutMessage: string
  ): Promise<T> =>
    new Promise((resolve, reject) => {
      const cleanup = () => {
        clearTimeout(timerId)
        window.removeEventListener('message', onMessage)
      }
      const fail = (err: Error) => {
        logger.error(err.message)
        cleanup()
        reject(err)
      }
      const onMessage = (event: MessageEvent) => {
        try {
          const eventInfo = event.data
          if (eventInfo.type === CHILIPIPER_DEANON_EVENT) {
            const data = JSON.parse(eventInfo.data) as WebDataEvent
            const result = predicate(data, fail)
            if (result !== undefined) {
              cleanup()
              resolve(result)
            }
          }
        } catch (error) {
          captureException(error)
          cleanup()
          reject(error)
        }
      }

      // timerId is declared after onMessage but assigned before the listener is added,
      // so it is always defined by the time onMessage fires.
      const timerId = setTimeout(() => {
        window.removeEventListener('message', onMessage)
        reject(new Error(timeoutMessage))
      }, timeoutMs)

      window.addEventListener('message', onMessage)
    })

  const initPromise = waitForMessage<string>(
    (data, fail) => {
      if (data.type === 'IFRAME_READY') {
        logger.log('Chili Web Data Iframe is ready')
        return data.guestId
      }
      if (data.type === 'IFRAME_ERROR') {
        fail(new Error('Error loading Chili Web Data iframe (guest session)'))
      }
    },
    2_000,
    'Timed out waiting for iframe initialization (guest session)'
  )

  const analyticsSettingsPromise = new Promise<AnalyticsSettingsEvent['settings']>(
    (resolve, reject) => {
      initPromise
        .then(() => {
          waitForMessage<AnalyticsSettingsEvent['settings']>(
            (data, fail) => {
              if (data.type === 'Analytics_Settings') {
                logger.log('Loaded Analytics Settings')
                return data.settings
              }
              if (data.type === 'Analytics_Settings_Error') {
                fail(new Error('Error loading Deanonymization Settings'))
              }
            },
            2_000,
            'Timed out waiting for Analytics Settings'
          )
            .then(resolve)
            .catch(reject)
        })
        // Intentionally swallowed: errors from initPromise must not propagate to the host site's error handlers.
        .catch(() => {})
    }
  )

  // Unlike the one-shot waits above this listener stays on: the decision can change with the
  // visitor's consent, and it arrives before IFRAME_READY, since the iframe decides as it starts.
  const onConsentDecision = (callback: (enforce: boolean) => void): (() => void) => {
    const onMessage = (event: MessageEvent) => {
      const envelope: unknown = event.data
      if (typeof envelope !== 'object' || envelope === null) return
      if (!('type' in envelope) || envelope.type !== CHILIPIPER_DEANON_EVENT) return
      if (!('data' in envelope) || typeof envelope.data !== 'string') return

      let decision: unknown
      try {
        decision = JSON.parse(envelope.data)
      } catch {
        return
      }
      if (typeof decision !== 'object' || decision === null) return
      if (!('type' in decision) || decision.type !== 'Consent_Decision') return
      if (!('enforce' in decision) || typeof decision.enforce !== 'boolean') return

      callback(decision.enforce)
    }

    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }

  return {
    sendEvent,
    initPromise,
    analyticsSettingsPromise,
    onConsentDecision,
  }
}
